import React, { useEffect, useState } from "react";
import { X, Check, Vote, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * PollNudge — non-blocking corner card that surfaces the current active poll
 * to every visitor (auth OR anon). One-tap vote right inside the card so the
 * visitor never leaves whatever they were doing (searching for a campsite,
 * reading a story, etc). Dismissable for 7 days per browser.
 *
 * Placement: bottom-right on desktop (sits ABOVE the AI chat bubble via
 * `bottom-24`), bottom-inset on mobile (`bottom-4`). Fully translucent
 * backdrop so it never blocks content — no modal.
 *
 * State machine:
 *   idle       — poll question + options showing
 *   voting     — spinner while POST /api/polls/{id}/vote is in flight
 *   thanks     — 3-second "Thanks!" confirmation then auto-dismiss
 *   dismissed  — hidden until this cycle's poll rotates away
 */
// iter168 — Dismiss key is now scoped to the CURRENT poll_id+week_index
// so every new cycle (daily rotation) surfaces a fresh nudge. The old
// blanket "ccu:poll-nudge:dismissed" key locked the nudge for 7 days
// regardless of which poll was live — meaning campers who voted once
// never saw the next day's poll.
const dismissKey = (poll) => `ccu:poll-nudge-seen:${poll.poll_id}:${poll.week_index}`;
const SHOW_DELAY_MS = 5000;

export default function PollNudge() {
  const [poll, setPoll] = useState(null);
  const [visible, setVisible] = useState(false);
  const [stage, setStage] = useState("idle");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // Fetch the current active poll
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/polls/today");
        if (cancel) return;
        // /polls/today returns the poll directly (not wrapped)
        if (data && Array.isArray(data.options) && data.poll_id) {
          // Already dismissed THIS cycle's poll?
          try {
            if (localStorage.getItem(dismissKey(data))) return;
          } catch { /* private mode — fail open */ }
          setPoll(data);
          // Show after a delay so it doesn't fight the hero for attention
          setTimeout(() => { if (!cancel) setVisible(true); }, SHOW_DELAY_MS);
        }
      } catch { /* no active poll — silently skip */ }
    })();
    return () => { cancel = true; };
  }, []);

  const dismiss = () => {
    try { if (poll) localStorage.setItem(dismissKey(poll), "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  const submitVote = async (optionKey) => {
    if (stage === "voting") return;
    setSelected(optionKey);
    setStage("voting");
    try {
      await api.post(`/polls/${poll.poll_id}/vote`, { option: optionKey });
      setStage("thanks");
      // Auto-dismiss after 3s AND mark THIS cycle as seen
      setTimeout(() => {
        try { localStorage.setItem(dismissKey(poll), "1"); } catch { /* ignore */ }
        setVisible(false);
      }, 3000);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't record vote — try again?";
      toast.error(msg);
      setStage("idle");
      setSelected(null);
    }
  };

  if (!visible || !poll) return null;

  return (
    <div
      className="fixed z-40 bottom-4 sm:bottom-24 right-4 sm:right-6 max-w-[calc(100vw-32px)] sm:max-w-sm"
      data-testid="poll-nudge"
      role="region"
      aria-label="Vote in the current poll"
    >
      <div className="bg-white border-2 hairline-strong rounded-2xl shadow-[0_10px_0_0_#0e0e0e] p-4 sm:p-5 relative">
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-white hover:bg-[#fff4ee] text-[#666] hover:text-[#ff4500] transition-colors"
          aria-label="Dismiss poll"
          data-testid="poll-nudge-dismiss"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>

        {stage === "thanks" ? (
          <div className="text-center py-4" data-testid="poll-nudge-thanks">
            <div className="w-12 h-12 rounded-full bg-[#059669] text-white mx-auto mb-2 flex items-center justify-center">
              <Check className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <div className="font-display text-lg leading-tight mb-1">Thanks — vote counted.</div>
            <p className="text-xs text-[#666]">Results shape next week&rsquo;s features.</p>
          </div>
        ) : (
          <>
            <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-2 pr-6">
              <Vote className="w-3 h-3" /> Quick poll · 1 tap
            </div>
            <h3 className="font-display text-base sm:text-lg leading-tight mb-3" data-testid="poll-nudge-question">
              {poll.question}
            </h3>
            <div className="space-y-1.5" data-testid="poll-nudge-options">
              {(poll.options || []).slice(0, 5).map((o) => {
                const busy = stage === "voting" && selected === o.key;
                return (
                  <button
                    key={o.key}
                    onClick={() => submitVote(o.key)}
                    disabled={stage === "voting"}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border hairline text-left text-sm transition-colors ${busy ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white hover:bg-[#fff4ee] hover:border-[#ff4500] text-[#0e0e0e]"} disabled:opacity-60`}
                    data-testid={`poll-nudge-option-${o.key}`}
                  >
                    <span className="truncate">{o.label}</span>
                    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] mono text-[#888] mt-3 leading-relaxed">
              Anonymous · one vote per browser · no signup needed.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
